import React, {ReactNode, Suspense, useContext} from 'react';

import ComponentErrorBoundary from 'coreComponents/ComponentErrorBoundary';
import type {CacheTagManager} from 'coreHooks/useSSRResponse';
import {ServerContext} from 'coreUtils/adapters/sdk';

import NonCriticalContext from './NonCriticalContext';

/** Searched in the server `onError` component stack (`react-router/entry.server.tsx`). */
export const NON_CRITICAL_BOUNDARY_MARKER = 'NonCriticalBoundaryMarker';

/**
 * Transparent marker. Named function: React builds production component stacks from function names
 * (the SSR build is not minified).
 * @param {object} props - props
 * @param {ReactNode} props.children - children
 * @returns {React.ReactElement}
 */
function NonCriticalBoundaryMarker({children}: {children: ReactNode}) {
	return <NonCriticalContext.Provider value>{children}</NonCriticalContext.Provider>;
}

NonCriticalBoundaryMarker.displayName = NON_CRITICAL_BOUNDARY_MARKER;

/** Cache tags of the content rendered below the boundary, by type (ids). */
export interface NonCriticalCacheTags {
	contentful?: (string | undefined)[];
	product?: (string | undefined)[];
	category?: (string | undefined)[];
}

interface NonCriticalBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	/**
	 * Server: added to the response when the boundary renders, in the shell. The content below is
	 * streamed after the headers, so its own tags would be lost (`coreUtils/ssrShell`).
	 */
	cacheTags?: NonCriticalCacheTags;
}

const useDeclareCacheTags = (cacheTags?: NonCriticalCacheTags) => {
	const ctx = useContext(ServerContext) as {res?: {cacheTagManager?: CacheTagManager}} | null;
	const manager = ctx?.res?.cacheTagManager;

	if (manager && cacheTags) {
		cacheTags.contentful?.filter(Boolean).forEach((id) => manager.addContentfulTag(id));
		cacheTags.product?.filter(Boolean).forEach((id) => manager.addProductTag(id));
		cacheTags.category?.filter(Boolean).forEach((id) => manager.addCategoryTag(id));
	}
};

/**
 * Boundary for page parts the page can live without (editorial modules, recommendations).
 *
 * - Client: errors are caught and logged by `ComponentErrorBoundary`; while suspending, `fallback` is shown.
 * - Server: error boundaries do not run during SSR. An error thrown below this boundary is logged and
 *   the boundary is rendered on the client instead of failing the whole page (see `onError` in
 *   `react-router/entry.server.tsx`, Vite brands only).
 * - Server, Vite brands: the content does not hold the shell, it is streamed after it when not ready
 *   (`coreUtils/ssrShell`). Its cache tags go in `cacheTags`.
 */
const NonCriticalBoundary = ({children, fallback = null, cacheTags}: NonCriticalBoundaryProps): React.ReactElement => {
	useDeclareCacheTags(cacheTags);

	return (
		<Suspense fallback={fallback}>
			<ComponentErrorBoundary>
				<NonCriticalBoundaryMarker>{children}</NonCriticalBoundaryMarker>
			</ComponentErrorBoundary>
		</Suspense>
	);
};

export default NonCriticalBoundary;
